/**
 * BarcodeScannerSheet - 書籍の ISBN バーコードをカメラで読み取る BottomSheet
 *
 * ZXing で EAN-13 のみをデコードし、978 / 979 で始まるコードを ISBN として親へ返す。
 * 日本の書籍は下段に分類・価格コード (192 始まり) が並ぶため、そちらを読んだときは
 * 採用せずガイド文言を出して読み直してもらう。
 *
 * ZXing のバンドルは重いので、シートを開いたときに動的 import する。
 */

import type { IScannerControls } from '@zxing/browser'
import type { DecodeHintType } from '@zxing/library'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { BottomSheet } from '@/components/ui/bottom-sheet'

import { extractIsbnFromBarcode } from '../lib/isbn-barcode'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** ISBN を読み取ったときに呼ばれる (13 桁の数字) */
  onDetected: (isbn: string) => void
}

type ScanStatus = 'starting' | 'scanning' | 'error'

/** getUserMedia の DOMException.name から日本語メッセージを引く */
const CAMERA_ERROR_MESSAGES = new Map<string, string>([
  ['NotAllowedError', 'カメラの使用が許可されていません。ブラウザの設定で許可してください。'],
  ['NotFoundError', '使用できるカメラが見つかりませんでした。'],
  ['NotReadableError', 'カメラを他のアプリが使用中の可能性があります。'],
  ['OverconstrainedError', '対応するカメラが見つかりませんでした。'],
])

const toErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    const known = CAMERA_ERROR_MESSAGES.get(error.name)
    if (known) return known
  }
  if (!window.isSecureContext) {
    return 'HTTPS でないためカメラを起動できませんでした。'
  }
  // ZXing は映像の再生が tryPlayVideoTimeout 内に始まらないと Error ではなく false で reject する
  if (error === false) {
    return 'カメラの映像を再生できませんでした。'
  }
  const detail = error instanceof Error ? `${error.name}: ${error.message}` : String(error)
  return `カメラを起動できませんでした。(${detail})`
}

export const BarcodeScannerSheet = ({ open, onOpenChange, onDetected }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  // 読み取り成功後もデコードコールバックが数回走るため、二重通知をここで止める
  const detectedRef = useRef(false)
  const [status, setStatus] = useState<ScanStatus>('starting')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [hint, setHint] = useState<string | null>(null)

  // onDetected は親で毎レンダー再生成されうるので、カメラを再起動させないよう ref 経由で呼ぶ
  const onDetectedRef = useRef(onDetected)
  useEffect(() => {
    onDetectedRef.current = onDetected
  }, [onDetected])

  useEffect(() => {
    if (!open) return
    const video = videoRef.current
    if (!video) return

    detectedRef.current = false
    setStatus('starting')
    setErrorMessage(null)
    setHint(null)

    let cancelled = false
    let controls: IScannerControls | null = null

    const start = async () => {
      try {
        const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType: HintType }] = await Promise.all([
          import('@zxing/browser'),
          import('@zxing/library'),
        ])
        if (cancelled) return

        const hints = new Map<DecodeHintType, unknown>()
        hints.set(HintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13])
        hints.set(HintType.TRY_HARDER, true)
        const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 100 })

        controls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: 'environment' } } },
          video,
          (result) => {
            if (!result || detectedRef.current) return
            const isbn = extractIsbnFromBarcode(result.getText())
            if (!isbn) {
              setHint('ISBN のバーコード (978 で始まる方) を枠に入れてください')
              return
            }
            detectedRef.current = true
            navigator.vibrate?.(60)
            onDetectedRef.current(isbn)
          },
        )
        if (cancelled) {
          controls.stop()
          controls = null
          return
        }
        setStatus('scanning')
      } catch (error) {
        if (cancelled) return
        setErrorMessage(toErrorMessage(error))
        setStatus('error')
      }
    }

    void start()

    return () => {
      cancelled = true
      controls?.stop()
    }
  }, [open])

  return (
    <BottomSheet title="バーコードで検索" open={open} onOpenChange={onOpenChange} snapPoints={[0.8]}>
      <div className="flex flex-col gap-3 px-4 pb-4">
        <div className="relative aspect-4/3 w-full overflow-hidden rounded-lg bg-black">
          <video ref={videoRef} className="size-full object-cover" muted playsInline />

          {/* 読み取り位置のガイド枠 */}
          <div className="pointer-events-none absolute inset-x-6 top-1/2 h-24 -translate-y-1/2 rounded border-2 border-white/80" />

          {status === 'starting' && (
            <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/60 text-sm text-white">
              <Loader2 className="size-4 animate-spin" />
              カメラを起動しています…
            </div>
          )}

          {status === 'error' && errorMessage && (
            <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-white">
              {errorMessage}
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground" aria-live="polite">
          {hint ?? '本の裏表紙にある上段のバーコード (978 で始まる ISBN) を枠に合わせてください。'}
        </p>
      </div>
    </BottomSheet>
  )
}
